﻿import {isNullOrEmpty, translation} from "@2d-front-monorepo/core";
import {Button, Input, OptionList, OptionListEntry, Select} from "@2d-front-monorepo/design-library";
import {EnumLists, GroupedPermit, TariffId, Vehicles} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, useCallback, useContext, useMemo, useState} from "react";
import {usePermits} from "../../hooks/usePermits";
import {useFilteredItems} from "./FilteredItemsProvider";
import {FilterManager, useFilter} from "./FilterManager";
import {FilterEntry, FilterPopoverContext, FiltersComponent} from "./FiltersComponent";

function StatusFilter(): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [status, setStatus] = useState<string[]>([]);
	const filterFn = useCallback((items: GroupedPermit[]) => {
			return (status.length > 0 ? items.filter(item => status.includes(item.permit.status.type.toString(10))) : items);
		}
		, [status]);

	const entries = useMemo(() => {
		const result: OptionListEntry[] = [];
		for (const {label, value} of EnumLists.permitStatusType()) {
			result.push({
				label: label,
				value: value.toString(10)
			});
		}

		return result;
	}, []);

	useFilter("status", filterFn);

	return (
		<div className="layout vertical padded inline">
			<OptionList name="status" values={status} items={entries} multiple={true} onChange={selected => {
				setStatus(selected.map(x => entries[x].value))
			}}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>);
}

function PlateFilter(): JSX.Element {
	const popover = useContext(FilterPopoverContext);
	const [plate, setPlate] = useState<string>("");
	const {permits} = usePermits();

	const filterFn = useCallback((items: GroupedPermit[]) => {
		const p = plate.trim().toUpperCase();
		return p
			? items.filter(item =>
				Vehicles.getValid(permits, item.permit.vehicles)
					.map(x => x.entity.plate.toUpperCase()).some(x => x.includes(p)))
			: items;
	}, [plate, permits]);

	useFilter("plate", filterFn);

	return (
		<div className="layout vertical padded inline">
			<Input label={translation(PermitTranslations.summary_page__filters__plate_label)}
			       name="plate" type="search"
			       value={plate}
			       onChange={e => setPlate(e.target.value ?? "")}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>);
}

function TariffFilter(): JSX.Element {
	const {holderData} = usePermits();
	const popover = useContext(FilterPopoverContext);
	const [tariff, setTariff] = useState<TariffId>("" as TariffId);
	const tariffs = useMemo(() => holderData.tariffs.map(x => ({
		label: x.title,
		value: x.type
	})), [holderData]);

	const filterFn = useCallback((items: GroupedPermit[]) =>
		isNullOrEmpty(tariff)
			? items
			: items.filter(item => item.permit.tariffId === tariff), [tariff]);

	useFilter("tariff", filterFn);

	return (
		<div className="layout vertical padded inline">
			<Select label={translation(PermitTranslations.permit_type)}
			        name="tariff"
			        items={tariffs}
			        placeholder={translation(PermitTranslations.summary_page__filters__tariff_placeholder)}
			        onChange={e => {
				        const selected = (e.target.selectedIndex ?? 0) - 1;
				        setTariff((selected === -1 ? undefined : tariffs[selected].value) as TariffId);
			        }}/>

			<div className="form__buttons">
				<Button kind="primary" onClick={() => popover?.close()}>
					{translation(PermitTranslations.done)}
				</Button>
			</div>
		</div>
	);
}

export function PermitsFilterComponent(): JSX.Element {
	const {source, setFiltered} = useFilteredItems<GroupedPermit>();

	return (
		<FilterManager source={source} onFiltered={setFiltered}>
			<FiltersComponent title={translation(PermitTranslations.summary_page__filters)}>
				<FilterEntry title={translation(PermitTranslations.summary_page__filters__status_title)}
				             label={translation(PermitTranslations.summary_page__filters__status_label)}><StatusFilter/></FilterEntry>
				<FilterEntry title={translation(PermitTranslations.summary_page__filters__plate_title)}
				             label={translation(PermitTranslations.summary_page__filters__plate_label)}><PlateFilter/></FilterEntry>
				<FilterEntry title={translation(PermitTranslations.summary_page__filters__tariff_title)}
				             label={translation(PermitTranslations.summary_page__filters__tariff_label)}><TariffFilter/></FilterEntry>
			</FiltersComponent>
		</FilterManager>
	)
}
